import { create } from 'zustand';
import lunchService from '../api/services/lunch.service';
import { showToast } from './useToastStore';
import { DAY_NAMES } from '../utils/constants';

let lunchOptSeq = 100;
let loadSeq = 0;

const emptyOption = () => ({ id: ++lunchOptSeq, text: '', tags: [] });

/* Week skeleton — the days the school week has, each with nothing on it until
   the API says otherwise. */
const emptyWeek = () =>
  Object.fromEntries(Object.keys(DAY_NAMES).map((d) => [d, { breakfast: [], lunch: [] }]));

const useLunchStore = create((set, get) => ({
  /* The Lunch Menu page's editor state. The dashboard widget fetches its own
     read-only copy (LunchWeekWidget), so nothing typed here shows there until
     it's saved. */
  menu: emptyWeek(),
  activeDay: 'Mon',
  dayLoading: false,
  saving: false,

  setActiveDay: (day) => set({ activeDay: day }),

  /* Autofill one day from the API (called on page load + every day switch).
     No saved menu for that day → a blank editor (one empty option per meal). */
  loadDay: async (day, schoolId) => {
    if (!schoolId) return;
    const reqId = ++loadSeq;
    set({ dayLoading: true });
    try {
      const saved = await lunchService.getDay(day, schoolId);
      const next = {
        breakfast: saved?.breakfast?.length ? saved.breakfast : [emptyOption()],
        lunch: saved?.lunch?.length ? saved.lunch : [emptyOption()],
      };
      set({ menu: { ...get().menu, [day]: next } });
    } catch (err) {
      showToast(err.message);
    } finally {
      if (reqId === loadSeq) set({ dayLoading: false });
    }
  },

  /* Persists the currently edited day; returns true on success. */
  saveDay: async (schoolId) => {
    const { activeDay, menu } = get();
    if (!schoolId) return true;
    set({ saving: true });
    try {
      await lunchService.saveDay(activeDay, schoolId, menu[activeDay]);
      return true;
    } catch (err) {
      showToast(err.message);
      return false;
    } finally {
      set({ saving: false });
    }
  },

  updateOptionText: (meal, id, text) => {
    const { menu, activeDay } = get();
    set({
      menu: {
        ...menu,
        [activeDay]: {
          ...menu[activeDay],
          [meal]: menu[activeDay][meal].map((o) => (o.id === id ? { ...o, text } : o)),
        },
      },
    });
  },

  toggleOptionTag: (meal, id, tagId) => {
    const { menu, activeDay } = get();
    set({
      menu: {
        ...menu,
        [activeDay]: {
          ...menu[activeDay],
          [meal]: menu[activeDay][meal].map((o) =>
            o.id === id
              ? { ...o, tags: o.tags.includes(tagId) ? o.tags.filter((t) => t !== tagId) : [...o.tags, tagId] }
              : o
          ),
        },
      },
    });
  },

  addOption: (meal) => {
    const { menu, activeDay } = get();
    set({
      menu: {
        ...menu,
        [activeDay]: {
          ...menu[activeDay],
          [meal]: [...menu[activeDay][meal], { id: ++lunchOptSeq, text: '', tags: [] }],
        },
      },
    });
  },

  removeOption: (meal, id) => {
    const { menu, activeDay } = get();
    set({
      menu: {
        ...menu,
        [activeDay]: {
          ...menu[activeDay],
          [meal]: menu[activeDay][meal].filter((o) => o.id !== id),
        },
      },
    });
  },
}));

export const todayLunchDay = () => {
  const map = { 1: 'Mon', 2: 'Tue', 3: 'Wed', 4: 'Thu', 5: 'Fri' };
  return map[new Date().getDay()] || 'Mon';
};

export default useLunchStore;
